<template>
  <v-container fluid class="pa-0">
    <!-- Hero Header -->
    <v-sheet color="primary" dark class="hero-header pa-6 pa-md-10">
      <v-row align="center" justify="center">
        <v-col cols="12" md="8" class="text-center">
          <v-avatar size="64" color="white" class="mb-4 elevation-4">
            <v-icon size="36" color="primary">mdi-message-text</v-icon>
          </v-avatar>
          <h1 class="text-h4 text-md-h3 font-weight-bold white--text mb-2">
            Message Templates
          </h1>
          <p class="text-subtitle-1 white--text text--lighten-3 mb-0">
            Manage your campaign message templates
          </p>
        </v-col>
      </v-row>
    </v-sheet>

    <v-container class="py-8">
      <v-row justify="center">
        <v-col cols="12" xl="10">

          <!-- Stats & Actions Bar -->
          <v-card class="mb-6 stats-bar" elevation="2" outlined>
            <v-card-text class="pa-4">
              <v-row align="center" dense>
                <v-col cols="12" sm="4" class="mb-2 mb-sm-0">
                  <div class="d-flex align-center">
                    <v-avatar size="40" color="primary lighten-5" class="mr-3">
                      <v-icon color="primary">mdi-message</v-icon>
                    </v-avatar>
                    <div>
                      <div class="text-caption grey--text">Total Templates</div>
                      <div class="text-h5 font-weight-bold primary--text">{{ messagesList.length }}</div>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" sm="5" class="mb-2 mb-sm-0">
                  <v-text-field
                    v-model="search"
                    prepend-inner-icon="mdi-magnify"
                    placeholder="Search messages..."
                    hide-details
                    dense
                    filled
                    rounded
                    class="search-field"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="3" class="text-sm-right">
                  <v-btn
                    color="primary"
                    depressed
                    rounded
                    :to="{ name: 'savings.add_marketing_messages' }"
                    class="add-btn"
                  >
                    <v-icon left>mdi-plus</v-icon>
                    <span class="hidden-xs-only">New Message</span>
                    <span class="hidden-sm-and-up">New</span>
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Messages Table -->
          <v-card elevation="2" class="messages-card">
            <v-data-table
              :headers="headers"
              :items="messagesList"
              item-key="id"
              class="elevation-0 messages-table"
              :items-per-page="10"
              :footer-props="{ itemsPerPageOptions: [10, 25, 50, 100] }"
              :search="search"
              loading-text="Loading messages..."
              no-data-text="No messages found"
              no-results-text="No matching messages"
            >
              <!-- Title / Subject -->
              <template v-slot:item.title="{ item }">
                <div class="d-flex flex-column py-2" @click="editMessageInfo(item.id)" style="cursor: pointer">
                  <span class="d-block font-weight-semibold text--primary text-truncate">{{ item.title }}</span>
                  <small class="grey--text">By: {{ item.uploader }}</small>
                </div>
              </template>

              <!-- Content preview -->
              <template v-slot:item.content="{ item }">
                <div class="text-truncate max-width-200 grey--text text--darken-1">
                  {{ item.content || 'No content' }}
                </div>
              </template>

              <!-- Download button -->
              <template v-slot:item.get_file="{ item }">
                <v-btn
                  v-if="item.get_file"
                  :href="item.get_file"
                  target="_blank"
                  color="primary"
                  elevation="2"
                  small
                  rounded
                  class="download-btn"
                >
                  <v-icon left x-small>mdi-download</v-icon>
                  <span class="hidden-xs-only">{{ item.size || 'Download' }}</span>
                </v-btn>
                <v-chip v-else x-small outlined color="grey">No file</v-chip>
              </template>

              <!-- Link -->
              <template v-slot:item.link="{ item }">
                <v-chip
                  v-if="item.link"
                  x-small
                  color="info"
                  outlined
                  class="link-chip"
                  :href="item.link"
                  target="_blank"
                  link
                >
                  <v-icon left x-small>mdi-open-in-new</v-icon>
                  <span class="text-truncate max-width-100">{{ item.link }}</span>
                </v-chip>
                <span v-else class="grey--text text--lighten-1">—</span>
              </template>

              <!-- Date -->
              <template v-slot:item.get_date_str="{ item }">
                <div class="d-flex align-center">
                  <v-icon x-small color="grey" class="mr-1">mdi-calendar</v-icon>
                  <span class="text-caption grey--text">{{ item.get_date_str }}</span>
                </div>
              </template>

              <!-- Actions -->
              <template v-slot:item.actions="{ item }">
                <div class="d-flex align-center action-group">
                  <v-btn
                    icon
                    small
                    color="primary"
                    @click="editMessageInfo(item.id)"
                    title="Edit message"
                  >
                    <v-icon small>mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    small
                    color="info"
                    :to="{ name: 'savings.add_marketing_messages', params: { cloneId: item.id } }"
                    title="Clone message"
                  >
                    <v-icon small>mdi-content-copy</v-icon>
                  </v-btn>
                </div>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Loading Overlay -->
    <v-overlay :value="loading" opacity="0.8" z-index="999">
      <v-card class="pa-6 text-center" max-width="320" rounded="lg">
        <v-progress-circular indeterminate color="primary" size="56" width="4" class="mb-4" />
        <div class="text-h6 font-weight-medium mb-1">Loading Messages</div>
        <div class="text-body-2 grey--text">Please wait...</div>
      </v-card>
    </v-overlay>
  </v-container>
</template>

<script>
import axios from 'axios'

export default {
  name: 'MessagesList',

  data() {
    return {
      loading: false,
      headers: [
        { text: 'Subject', value: 'title', sortable: false, width: '250px' },
        { text: 'Message', value: 'content', sortable: false, width: '300px' },
        { text: 'Download', value: 'get_file', sortable: false, align: 'center', width: '120px' },
        { text: 'Link', value: 'link', sortable: false, width: '150px' },
        { text: 'Date', value: 'get_date_str', sortable: false, width: '140px' },
        { text: '', value: 'actions', sortable: false, align: 'center', width: '100px' },
      ],
      messagesList: [],
      search: '',
    }
  },

  mounted() {
    this.getAdMessage()
    document.title = 'TopUp | Message Templates'
  },

  methods: {
    async getAdMessage() {
      this.loading = true
      try {
        const response = await axios.get('/api/v1/topup/get/messages_list/')
        this.messagesList = response.data
      } catch (error) {
        if (error.response) {
          this.showToast(error.response.status + ' ' + error.response.statusText, 'error')
        } else {
          this.showToast('An error occurred, please check your internet connection and try again later.', 'error')
        }
      } finally {
        this.loading = false
      }
    },

    editMessageInfo(info_id) {
      this.$router.push('/savings/marketing/edit/messages/' + info_id)
    },

    showToast(msg, type) {
      this.$store.commit('setSnackBarMessage', msg)
      this.$store.commit('setSnackBarColor', type === 'success' ? 'teal darken-4' : 'red darken-4')
      this.$store.commit('activateSnackBar', true)
    },
  },
}
</script>

<style scoped>
.hero-header {
  border-radius: 0 0 24px 24px;
  position: relative;
  overflow: hidden;
}
.hero-header::after {
  content: '';
  position: absolute;
  top: -50%;
  right: -20%;
  width: 60%;
  height: 200%;
  background: rgba(255,255,255,0.05);
  border-radius: 50%;
}

.stats-bar {
  border-radius: 16px;
}

.search-field {
  max-width: 100%;
}

.add-btn {
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: none;
}

.messages-card {
  border-radius: 16px;
  overflow: hidden;
}

.messages-table ::v-deep th {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(0,0,0,0.6) !important;
  font-weight: 600 !important;
  background: #f8f9fa !important;
}

.messages-table ::v-deep td {
  border-bottom: 1px solid rgba(0,0,0,0.04) !important;
}

.download-btn {
  text-transform: none;
  font-weight: 500;
}

.link-chip {
  max-width: 140px;
}

.action-group {
  gap: 4px;
}

.max-width-200 {
  max-width: 200px;
}

.max-width-100 {
  max-width: 100px;
}

@media (max-width: 600px) {
  .hero-header {
    border-radius: 0 0 16px 16px;
    padding: 24px 16px !important;
  }
  .stats-bar .col {
    margin-bottom: 8px;
  }
}
</style>
